// PARTNER-PORTÁL · jegy nyitása — a terv 3-lépéses vezetett űrlapja
// (a partner-terv szerint — a MEGÉPÜLT állapot: `docs/foundations/design.md` §9).
//
// A LÉPÉSEK: Hol? (ügyfél-blokk + fiók-választó) → Miről? (szolgáltatás két csoportban) →
// Részletek (JEGYTÍPUS · tárgy · leírás min. 25 látható karakterrel · mellékletek · fájl-megosztási linkek).
//
// A JEGYTÍPUS a 3. lépés ELEJÉN áll (user-döntés), nem a 2. végén. MIÉRT: a 2. lépés kérdése
// „MELYIK SZOLGÁLTATÁS" — egy radiogroup-lista, amit a fiók dönt el; a típus („támogatás / hibajelentés /
// …") viszont már a KONKRÉT ÜGYRŐL szól, tehát a tárgy és a leírás társa. A lépés végére tett választó
// mellékesnek látszott, és a szolgáltatás-lista görgetése alatt könnyen elmaradt.
//
// MIÉRT VEZETETT ŰRLAP a belső egy-lapos form helyett: a partner évente néhányszor nyit jegyet, és a
// döntéseinek SORRENDJE van (a fiók dönti el, mely szolgáltatások jönnek). A belső ügyintéző napi rutinja
// ellenkező: ott a sűrű, egy-lapos mezőlista a gyorsabb.
//
// AMI SZÁNDÉKOSAN MÁS A TERVHEZ KÉPEST: a „További szolgáltatásaink" csoport elemei a terv mockupjában
// „érdeklődés" jelöléssel szerepelnek — itt ugyanaz a lista, de a jelölés a szerver csoportosítását követi
// (`existing` = a fiók használja · `other` = a katalógus többi eleme), mert a jelentést az adja.
import { PORTAL_PERMISSIONS } from '@atlas/shared';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  BuildingOffice2Icon,
  CheckIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { DropboxMark, GoogleDriveMark, OneDriveMark } from '@/components/brand/service-marks';
import { PortalChoiceCard } from '@/components/portal/portal-choice-card';
import { PortalErrorState } from '@/components/portal/portal-error-state';
import { PortalPermissionGate } from '@/components/portal/portal-permission-gate';
import { PortalStepper } from '@/components/portal/portal-stepper';
import { DomainBadge } from '@/components/shared/domain-badge';
import { EmptyState } from '@/components/shared/empty-state';
import { PendingAttachmentsField } from '@/components/shared/pending-attachments-field';
import { TicketTypeBadge } from '@/components/tickets/ticket-type-badge';
import { Button } from '@/components/ui/button';
import { Divider } from '@/components/ui/divider';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { Skeleton } from '@/components/ui/skeleton';
import { Text } from '@/components/ui/text';
import { portalKeys } from '@/features/portal/api';
import { uploadPortalTicketAttachment } from '@/features/portal/attachment-upload';
import {
  PORTAL_NEW_TICKET_IMAGE_RESOLVER,
  uploadPortalEditorImage,
} from '@/features/portal/editor-images';
import { usePortal, usePortalHead } from '@/features/portal/portal-context';
import { PORTAL_TICKET_TYPES, type PortalTicketTypeKey } from '@/features/portal/ticket-types';
import { MAX_ATTACHMENTS } from '@/features/tickets/attachment-upload';
import { api } from '@/lib/api';
import { richTextToPlainText } from '@/lib/sanitize-html';
import { useEntityMutation } from '@/lib/use-entity-mutation';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/portal/tickets/new')({
  component: PortalNewTicketPage,
});

/**
 * A FÁJL-MEGOSZTÁSI LINK-mezők: kulcs · i18n-címke · márka-jel. Modul-szinten, mert a jel és a felirat
 * PÁRBAN tartozik össze — egy sorban felsorolva nem lehet a kettőt elcsúsztatni egymáshoz képest.
 */
const LINK_FIELDS = [
  { key: 'drive', labelKey: 'googleDrive', Mark: GoogleDriveMark },
  { key: 'onedrive', labelKey: 'onedrive', Mark: OneDriveMark },
  { key: 'dropbox', labelKey: 'dropbox', Mark: DropboxMark },
] as const;

/** A leírás minimuma LÁTHATÓ karakterben (legacy `min:25`). */
const DESCRIPTION_MIN = 25;
/** A tárgy plafonja (legacy `max:255`). */
const SUBJECT_MAX = 255;

/** A lap a jog-kapun át: jog nélkül magyarázat áll a lap helyett (a fal a BFF-ben van). */
function PortalNewTicketPage(): JSX.Element {
  return (
    <PortalPermissionGate permission={PORTAL_PERMISSIONS.ticketsWrite}>
      <PortalNewTicketPageBody />
    </PortalPermissionGate>
  );
}

function PortalNewTicketPageBody(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { me, accountId: contextAccountId } = usePortal();
  // MORZSA a topbarban („Jegyeim / Jegy nyitása") a lap-testbe tett „vissza a listához" gomb HELYETT
  // (user-döntés: „mindenhol így használjuk a rendszerben"). A visszaút a morzsa első elemén
  // van — ugyanaz a minta, mint a jegy-részleten (`portal.tickets.$id.tsx`).
  usePortalHead(
    {
      crumbs: [
        { label: t('portal.tickets.title'), to: '/portal/tickets' },
        { label: t('portal.newTicket.title') },
      ],
      subtitle: t('portal.newTicket.subtitle'),
      // `fill`: a lap KITÖLTI a keret magasságát, és a görgetés a lapon BELÜL történik — ez kell ahhoz, hogy
      // a LÉPÉS-JELZŐ és a GOMB-SÁV ragadós maradjon, és csak a köztes tartalom gördüljön (user-kérés).
      // A lista-oldal ugyanezt a módot használja.
      fill: true,
    },
    [t('portal.newTicket.title'), t('portal.tickets.title')],
  );

  const [step, setStep] = useState(0);
  const [accountId, setAccountId] = useState('');
  const [serviceId, setServiceId] = useState('');
  const [type, setType] = useState<PortalTicketTypeKey>('support');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [links, setLinks] = useState({ drive: '', onedrive: '', dropbox: '' });
  const [files, setFiles] = useState<File[]>([]);
  const [touched, setTouched] = useState({ subject: false, description: false });

  // A FIÓK-VÁLTÓ választása az alapértelmezés (a keret és a lap ugyanazt a fiókot mutatja), ÉS ilyenkor a
  // „Hol?" lépést ÁT IS LÉPJÜK: a kérdésre a sidebar már válaszolt, kétszer megkérdezni irritáló volt (ez
  // volt a user-lelet). A lépés nem tűnik el — a „Vissza" gombbal elérhető, tehát a fiók a lapon is
  // módosítható. „Minden fiók" scope-nál (`null`) a lépés marad, mert nincs mit örökölni.
  const skippedAccountStep = useRef(false);
  useEffect(() => {
    if (accountId !== '' || contextAccountId === null) return;
    setAccountId(contextAccountId);
    if (!skippedAccountStep.current) {
      skippedAccountStep.current = true;
      setStep((prev) => (prev === 0 ? 1 : prev));
    }
  }, [accountId, contextAccountId]);

  // FIÓK-VÁLTÁS → a szolgáltatás ürül (a korábbi választás MÁS fiókhoz tartozott).
  const prevAccount = useRef(accountId);
  useEffect(() => {
    if (prevAccount.current === accountId) return;
    prevAccount.current = accountId;
    setServiceId('');
  }, [accountId]);

  const optionsQ = useQuery({
    queryKey: portalKeys.ticketFormOptions(accountId === '' ? undefined : accountId),
    queryFn: () =>
      api.portal.ticketFormOptions.query({
        query: accountId === '' ? {} : { accountId },
      }),
  });
  const options = optionsQ.data?.status === 200 ? optionsQ.data.body : undefined;
  const isForbidden = optionsQ.data?.status === 403;
  const isError = optionsQ.data !== undefined && optionsQ.data.status !== 200 && !isForbidden;

  const accounts = options?.accounts ?? [];
  // A választható fiók a SZERVER jelzése (`canOpen` = `portal.tickets.write`), nem a szerep neve.
  const writable = accounts.filter((a) => a.canOpen);
  // EGYETLEN választható fiók → automatikus választás (a legtöbb partnernek egy fiókja van).
  useEffect(() => {
    if (writable.length === 1 && accountId === '') setAccountId(writable[0]?.id ?? '');
  }, [writable, accountId]);

  const mutation = useEntityMutation<{
    accountId: string;
    serviceId: string;
    type: PortalTicketTypeKey;
    subject: string;
    description: string;
    googleDriveLink: string | null;
    microsoftOnedriveLink: string | null;
    dropboxLink: string | null;
  }>({
    mutationFn: (v) => api.portal.ticketCreate.mutation({ body: v }),
    invalidateKeys: [portalKeys.tickets(), portalKeys.all],
    messages: {
      success: t('toast:portal.ticketCreated'),
      conflict: t('toast:portal.error'),
      forbidden: t('toast:portal.forbidden'),
      validation: t('toast:portal.validation'),
      error: t('toast:portal.error'),
    },
  });

  // A LÁTHATÓ hossz a MARKUP NÉLKÜLI szövegből (a szerkesztő HTML-t ad). Ez nem kozmetika: a szerver a
  // LÁTHATÓ karakterekre érvényesíti a 25-ös minimumot, tehát egy `<p></p>`-vel „kitöltött" leírás a
  // kliensen zöld lett volna, a mentésnél pedig 422 — épp a legrosszabb sorrendben.
  const descriptionLength = richTextToPlainText(description).length;
  const errors = {
    service: serviceId === '' ? t('portal.newTicket.errors.service') : null,
    subject:
      subject.trim() === ''
        ? t('portal.newTicket.errors.subject')
        : subject.length > SUBJECT_MAX
          ? t('portal.newTicket.errors.subjectMax', { max: SUBJECT_MAX })
          : null,
    description:
      descriptionLength < DESCRIPTION_MIN
        ? t('portal.newTicket.descriptionRemaining', { count: DESCRIPTION_MIN - descriptionLength })
        : null,
  };
  // A HÁROM lépés érvényessége EGY vektorban: ebből jön a „Tovább" gomb ÉS a stepper-navigáció engedélye
  // (két külön szabály itt csendben elcsúszott volna — a szám navigálhatott volna oda, ahonnan a „Tovább"
  // nem engedett).
  const stepsValid = [
    accountId !== '',
    errors.service === null,
    errors.subject === null && errors.description === null,
  ];
  const stepValid = stepsValid[step];

  const submit = (): void => {
    setTouched({ subject: true, description: true });
    if (errors.subject !== null || errors.description !== null || errors.service !== null) {
      setStep(2);
      return;
    }
    mutation.mutate(
      {
        accountId,
        serviceId,
        type,
        subject: subject.trim(),
        // A HTML-t NEM trimmeljük szövegként (a `<p>` burkolat a tartalom része) — a szerver a LÁTHATÓ
        // hosszra validál, azt a `plainTextLength` mérte a kliensen is.
        description,
        googleDriveLink: links.drive.trim() === '' ? null : links.drive.trim(),
        microsoftOnedriveLink: links.onedrive.trim() === '' ? null : links.onedrive.trim(),
        dropboxLink: links.dropbox.trim() === '' ? null : links.dropbox.trim(),
      },
      {
        onSuccess: (res) => {
          // A frissen nyitott jegy RÉSZLETÉRE megyünk (ott folytatható a beszélgetés).
          const body = res.body as { id?: unknown };
          const id = typeof body.id === 'string' ? body.id : '';
          const pending = files;
          setFiles([]);
          // A MELLÉKLETEK A JEGY LÉTREJÖTTE UTÁN mennek fel, a jegy id-jére (a belső jegy-nyitó ugyanígy):
          // a melléklet-sor a jegyhez tartozik, tehát előbb léteznie kell. A navigáció NEM várja meg — a
          // partner azonnal a jegyén van —, a KIMENETET viszont igen: ha egy fájl elhasalt, azt NÉVEN
          // NEVEZVE mondjuk el (a jegy attól még nyitva van, és a részleten újra fel tudja tölteni).
          if (id !== '' && pending.length > 0) {
            void (async () => {
              const failed: string[] = [];
              for (const file of pending) {
                if ((await uploadPortalTicketAttachment(id, file)) !== 'ok') failed.push(file.name);
              }
              if (failed.length > 0) {
                toast.error(t('toast:portal.attachmentPartial', { files: failed.join(', ') }));
              } else {
                toast.success(t('toast:portal.attachmentUploaded'));
              }
            })();
          }
          void navigate(
            id === '' ? { to: '/portal/tickets' } : { to: '/portal/tickets/$id', params: { id } },
          );
        },
      },
    );
  };

  if (isForbidden) {
    return (
      <EmptyState title={t('portal.noAccess.title')} description={t('portal.noAccess.desc')} />
    );
  }
  if (isError) {
    return <PortalErrorState onRetry={() => void optionsQ.refetch()} />;
  }
  // KI SEM SZOLGÁLHATÓ (nem átmeneti hiba): a szolgáltatás-katalógus gépi Odin-kredenciál nélkül nem
  // olvasható, a `serviceId` viszont KÖTELEZŐ a nyitáshoz. Ilyenkor magyarázatot adunk, nem „Újra" gombot —
  // az újratöltés ettől soha nem lesz jobb (user-mérés: a lap „Nem sikerült betölteni"-t írt).
  if (options?.unavailable === true) {
    return (
      <EmptyState
        icon={ExclamationTriangleIcon}
        title={t('portal.newTicket.unavailable.title')}
        description={t('portal.newTicket.unavailable.desc')}
      />
    );
  }
  if (optionsQ.isLoading) {
    return (
      <div className="w-full max-w-3xl space-y-4">
        <Skeleton className="h-10 w-2/3" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }
  // Van hozzáférése, de MINDEN fiókján csak megtekintő — a nyitás nem indulhat el, és ezt ki kell mondani.
  if (options !== undefined && accounts.length > 0 && writable.length === 0) {
    return (
      <EmptyState
        title={t('portal.newTicket.readOnly.title')}
        description={t('portal.newTicket.readOnly.desc')}
      />
    );
  }

  const client = me?.accounts.find((a) => a.id === accountId) ?? me?.accounts[0];

  return (
    // HÁROM SÁV: fix LÉPÉS-JELZŐ · gördülő TARTALOM · fix GOMB-SÁV. A `min-h-0` a flex-gyerekeken NEM
    // elhagyható: nélküle a gördülő sáv a tartalmához nőne, a `overflow-y-auto` sosem aktiválódna, és a
    // gomb-sáv lecsúszna a viewportról (ez a klasszikus flexbox-csapda).
    <div className="flex h-full min-h-0 w-full max-w-3xl flex-col">
      <PortalStepper
        steps={[
          t('portal.newTicket.steps.where'),
          t('portal.newTicket.steps.what'),
          t('portal.newTicket.steps.details'),
        ]}
        current={step}
        className="shrink-0"
        ariaLabel={t('portal.newTicket.stepsAria')}
        // A LÉPÉS-SZÁMOK NAVIGÁLNAK (user-kérés). VISSZA szabadon; ELŐRE csak addig, ameddig az
        // ÚTBAN LÉVŐ lépések érvényesek — különben a partner egy hiányos „Hol?"-ból ugorhatna a részletekre,
        // és a mentés a semmiből dobna hibát. A `reachable` ezt EGY helyen mondja ki (a „Tovább" gomb
        // ugyanezt a `stepValid`-ot használja).
        onStepSelect={(target) => {
          if (target <= step) {
            setStep(target);
            return;
          }
          if (stepsValid.slice(step, target).every(Boolean)) setStep(target);
        }}
        stepEnabled={(target) => target <= step || stepsValid.slice(step, target).every(Boolean)}
      />

      {/* A GÖRDÜLŐ SÁV. A `pr-0.5` + `-mr-0.5` a görgetősáv helyét adja vissza a tartalomnak, hogy a mezők
          jobb széle NE csússzon el a lépés-jelzőhöz és a gombokhoz képest. */}
      <div className="scrollbar-none min-h-0 flex-1 overflow-y-auto pb-1">
        {step === 0 && (
          <div className="space-y-6">
            <div>
              <Text variant="overline" as="div" className="mb-2.5">
                {t('portal.newTicket.client')}
              </Text>
              <div className="border-border bg-card flex items-center gap-2.5 rounded-[9px] border px-3.5 py-3">
                <span className="bg-brand-soft text-brand flex size-7 shrink-0 items-center justify-center rounded-lg">
                  <BuildingOffice2Icon className="size-4" />
                </span>
                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="truncate text-sm font-medium">{client?.clientName ?? '—'}</span>
                  <Text variant="secondary" as="span" className="text-[11px]">
                    {t('portal.newTicket.clientHint')}
                  </Text>
                </span>
                <DomainBadge
                  kind="marker"
                  value="accessGranted"
                  label={t('portal.newTicket.hasAccess')}
                />
              </div>
            </div>

            <div>
              <Text variant="overline" as="div" className="mb-2.5">
                {t('portal.newTicket.whichAccount')}
              </Text>
              <div
                role="radiogroup"
                aria-label={t('portal.newTicket.whichAccount')}
                className="space-y-2"
              >
                {accounts.map((a) => (
                  <PortalChoiceCard
                    key={a.id}
                    selected={accountId === a.id}
                    onSelect={() => a.canOpen && setAccountId(a.id)}
                    title={a.name}
                    subtitle={[
                      a.clientName ?? '',
                      me?.accounts.find((x) => x.id === a.id)?.isPrimary
                        ? t('portal.newTicket.youArePrimary')
                        : '',
                    ]
                      .filter((x) => x !== '')
                      .join(' · ')}
                    {...(!a.canOpen
                      ? {
                          right: (
                            <DomainBadge
                              kind="marker"
                              value="viewerOnly"
                              label={t('portal.newTicket.viewerOnly')}
                            />
                          ),
                        }
                      : {})}
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-6">
            {(['existing', 'other'] as const).map((group) => {
              const items = options?.services?.[group] ?? [];
              if (items.length === 0) return null;
              return (
                <div key={group}>
                  <Text variant="overline" as="div" className="mb-2.5">
                    {t(
                      `portal.newTicket.${group === 'existing' ? 'servicesOfAccount' : 'otherServices'}`,
                    )}
                  </Text>
                  <div
                    role="radiogroup"
                    aria-label={t(
                      `portal.newTicket.${group === 'existing' ? 'servicesOfAccount' : 'otherServices'}`,
                    )}
                    className="space-y-2"
                  >
                    {items.map((s) => (
                      <PortalChoiceCard
                        key={s.id}
                        selected={serviceId === s.id}
                        onSelect={() => setServiceId(s.id)}
                        title={s.name}
                        {...(group === 'existing'
                          ? {
                              right: (
                                <DomainBadge
                                  kind="marker"
                                  value="accessGranted"
                                  label={t('portal.newTicket.activeService')}
                                />
                              ),
                            }
                          : {})}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
            {(options?.services?.existing.length ?? 0) + (options?.services?.other.length ?? 0) ===
              0 && (
              <Text variant="secondary" as="p">
                {t('portal.newTicket.noServices')}
              </Text>
            )}
          </div>
        )}

        {step === 2 && (
          // SŰRŰBB blokk-térköz, mint az első két lépésen (user-lelet: „a tárgy és a kérés felett
          // túl nagyok a térközök"). MIÉRT MÁS ITT: az 1–2. lépés választó-KÁRTYÁKAT sorol (ott a levegő
          // segít a szkennelésben), a 3. viszont EGY ŰRLAP — a mezők közti 24px ott szétszakította a
          // címke↔mező párokat, és a lap fölöslegesen hosszú lett.
          <div className="space-y-4">
            <div>
              <Label className="mb-1.5 block">
                {t('portal.newTicket.type')}
                <span className="text-destructive"> *</span>
              </Label>
              {/* A TÍPUS-választó a LISTA-oldali badge-ekkel azonos megjelenésű (ugyanaz a szín-kód és ikon) —
                így a választó és a lista ugyanazt a nyelvet beszéli. A 3. lépés ELEJÉN áll: a típus a
                konkrét ügyről szól (a tárgy és a leírás társa), nem a szolgáltatás-választásról. */}
              <div
                role="radiogroup"
                aria-label={t('portal.newTicket.type')}
                className="flex flex-wrap gap-2"
              >
                {PORTAL_TICKET_TYPES.map((value) => {
                  const active = type === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      onClick={() => setType(value)}
                      className={cn(
                        'focus-visible:ring-ring rounded-full transition-opacity focus:outline-none focus-visible:ring-2',
                        active ? 'opacity-100' : 'opacity-45 hover:opacity-80',
                      )}
                    >
                      <TicketTypeBadge type={value} label={t(`portal.type.${value}`)} />
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="portal-subject">
                {t('portal.newTicket.subject')}
                <span className="text-destructive"> *</span>
              </Label>
              <Input
                id="portal-subject"
                value={subject}
                maxLength={SUBJECT_MAX}
                onChange={(e) => setSubject(e.target.value)}
                onBlur={() => setTouched((c) => ({ ...c, subject: true }))}
                placeholder={t('portal.newTicket.subjectPlaceholder')}
              />
              <div className="flex items-center justify-between gap-2">
                {touched.subject && errors.subject !== null ? (
                  <span className="text-destructive text-[11.5px]">{errors.subject}</span>
                ) : (
                  <span />
                )}
                <span
                  className={cn(
                    'text-[11.5px] tabular-nums',
                    subject.length > SUBJECT_MAX - 25 ? 'text-warning' : 'text-subtle',
                  )}
                >
                  {subject.length} / {SUBJECT_MAX}
                </span>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="portal-description">
                {t('portal.newTicket.description')}
                <span className="text-destructive"> *</span>
              </Label>
              {/* RICH-TEXT szerkesztő KÉPFELTÖLTÉSSEL (user-kérés, 2026-09-09: „a régi rendszerben is ez adott
                volt"). UGYANAZ a komponens, mint a belső jegy-nyitón — de a kép-útjai a PARTNER végpontjaira
                injektálva: ott a cél-fiók kötelező és a tenant a hívó portál-hozzáféréséből oldódik fel (a
                belső „fiók nélkül a MORGENS ügyfél a gazda" fallback a partnernél elvi hiba lenne). A
                feloldó a most feltöltött képeket a `commit` `displayUrl`-jéből szolgálja ki: a még nem
                létező jegy képéhez nincs mihez kötni egy szerver-oldali ellenőrzést. */}
              <RichTextEditor
                value={description}
                onChange={setDescription}
                onBlur={() => setTouched((c) => ({ ...c, description: true }))}
                placeholder={t('portal.newTicket.descriptionPlaceholder')}
                aria-label={t('portal.newTicket.description')}
                disabled={mutation.isPending}
                imageAccountId={accountId}
                uploadImage={(file) => uploadPortalEditorImage(file, accountId)}
                imageResolver={PORTAL_NEW_TICKET_IMAGE_RESOLVER}
              />
              <div className="flex items-center justify-between gap-2">
                <span
                  id="portal-desc-count"
                  aria-live="polite"
                  className={cn(
                    'text-[11.5px]',
                    descriptionLength < DESCRIPTION_MIN ? 'text-destructive' : 'text-subtle',
                  )}
                >
                  {descriptionLength < DESCRIPTION_MIN
                    ? t('portal.newTicket.descriptionRemaining', {
                        count: DESCRIPTION_MIN - descriptionLength,
                      })
                    : t('portal.newTicket.descriptionOk')}
                </span>
                <span className="text-subtle font-mono text-[11.5px] tabular-nums">
                  {descriptionLength} / min. {DESCRIPTION_MIN}
                </span>
              </div>
              {/* A HALADÁS-SÁV a minimumhoz mér: a partner látja, mennyi van még hátra (a terv eleme). */}
              <div className="bg-muted h-[3px] overflow-hidden rounded-full">
                <div
                  className={cn(
                    'ease-atlas h-full transition-[width] duration-200',
                    descriptionLength >= DESCRIPTION_MIN ? 'bg-brand' : 'bg-warning',
                  )}
                  style={{
                    width: `${String(Math.min(100, (descriptionLength / DESCRIPTION_MIN) * 100))}%`,
                  }}
                />
              </div>
              {/* A TIPP a blokk ALJÁN áll, a hossz-visszajelzés ALATT (user-döntés, 2026-09-09). Korábban a
                címke és a szerkesztő KÖZÉ volt szúrva: ott elvágta a címke↔mező párt, és épp a beírás
                helyét tolta lejjebb — egy tanács, amit a felhasználó a MÁSODIK jegynél már nem olvas el,
                nem foglalhat helyet a mező előtt. Alul viszont ott van, ha kell. */}
              <div className="bg-brand-soft mt-1 flex items-start gap-2.5 rounded-xl p-3 text-[12.5px] leading-relaxed">
                <InformationCircleIcon className="text-brand mt-0.5 size-4 shrink-0" />
                <span>{t('portal.newTicket.descriptionTip')}</span>
              </div>
            </div>

            {/* MELLÉKLETEK — a KÖZÖS mező (ugyanez a belső jegy-nyitón és az üzenet-íráson). A fájlok a jegy
              LÉTREJÖTTE UTÁN mennek fel a partner melléklet-végpontjaira (`features/portal/attachment-upload.ts`),
              mert a melléklet-sor a jegyhez tartozik. Bukásnál néven nevezett hibaüzenet megy — a jegy nyitva marad. */}
            <PendingAttachmentsField
              files={files}
              onChange={setFiles}
              disabled={mutation.isPending}
              labels={{
                title: t('portal.tickets.attachments'),
                add: t('portal.newTicket.attachmentsAdd'),
                remove: (name) => t('portal.newTicket.attachmentsRemove', { name }),
              }}
              messages={{
                limit: t('toast:portal.attachmentLimit', { max: MAX_ATTACHMENTS }),
                invalidType: t('toast:portal.attachmentInvalidType'),
                tooLarge: t('toast:portal.attachmentTooLarge'),
              }}
            />

            <div className="space-y-2">
              <Label className="block">{t('portal.tickets.links')}</Label>
              <Text variant="secondary" as="p" className="text-[11.5px]">
                {t('portal.newTicket.linksHint')}
              </Text>
              {/* A HÁROM MEZŐ a szolgáltatás JELÉVEL (nem kiírt felirattal) — a belső jegy-nyitó és az
                üzenet-írás „Linkek" blokkjának mintája (user-döntés, 2026-09-09). MIÉRT jobb az ikon: a
                132px-es szöveg-oszlop a mezőket keskenyre szorította épp ott, ahol egy hosszú URL-t kell
                elhelyezni; a márka-jel viszont AZONNAL azonosít, és a felirat megmarad a képernyőolvasónak
                (`aria-label`) meg a tooltipnek. */}
              {LINK_FIELDS.map(({ key, labelKey, Mark }) => (
                <Input
                  key={key}
                  value={links[key]}
                  onChange={(e) => setLinks((c) => ({ ...c, [key]: e.target.value }))}
                  placeholder={t('portal.newTicket.linkPlaceholder')}
                  aria-label={t(`portal.tickets.linkLabel.${labelKey}`)}
                  title={t(`portal.tickets.linkLabel.${labelKey}`)}
                  leadingIcon={<Mark className="size-4" />}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* A LÁBLÉC-ELVÁLASZTÓ a KÖZÖS `Divider` (behúzva) — nem `border-t` a gomb-sáv tetején. Két user-lelet
          egyszerre (2026-09-09): (a) a `border-t` KÖZVETLENÜL a gombokhoz ért, mert a szegély a gomb-sáv
          eleme volt, tehát a „térköz a vonal alatt" a sáv paddingja lett volna — a `Divider` `spacing`-je
          viszont a vonal MINDKÉT oldalán ad levegőt; (b) a `border-t` a lap SZÉLÉIG futott, holott a
          rendszer idiómája a behúzott hajszálvonal (a `Divider` alapból `mx-3`). */}
      <Divider spacing="md" className="shrink-0" />
      <div className="flex shrink-0 items-center gap-2">
        {step > 0 && (
          <Button variant="outline" onClick={() => setStep(step - 1)}>
            <ArrowLeftIcon />
            {t('portal.newTicket.prev')}
          </Button>
        )}
        <span className="flex-1" />
        <Button variant="ghost" onClick={() => void navigate({ to: '/portal/tickets' })}>
          {t('common.cancel')}
        </Button>
        {step < 2 ? (
          <Button variant="brand" disabled={!stepValid} onClick={() => setStep(step + 1)}>
            {t('portal.newTicket.next')}
            <ArrowRightIcon />
          </Button>
        ) : (
          <Button variant="brand" disabled={mutation.isPending} onClick={submit}>
            <CheckIcon />
            {mutation.isPending ? t('portal.tickets.sending') : t('portal.newTicket.submit')}
          </Button>
        )}
      </div>
    </div>
  );
}
